import { MachineStore } from "@infrastructure/stores/machine.store";
import { apiClients } from "@infrastructure/clients";
import { ServiceType } from "@domain/enums/machine.enums";
import { AuthStore } from "@infrastructure/stores/auth.store";
import { AuthService } from "@domain/services/auth.service";
import { when } from "mobx";
import { MachineItem, MachineListItem } from "@domain/dtos/models/machine.models";

class MachineServiceClass {
  private _initialized = false;
  private _isRequestingLocation = false;
  private _initPromise: Promise<void> | null = null;
  private _authSubscription: (() => void) | null = null;

  getStore() {
    return MachineStore;
  }

  async init() {
    // Если уже инициализирован, возвращаем успешный промис
    if (this._initialized) {
      return Promise.resolve();
    }

    // Если инициализация уже идет, возвращаем существующий промис
    if (this._initPromise) {
      return this._initPromise;
    }

    // Создаем промис для отслеживания процесса инициализации
    this._initPromise = (async () => {
      try {
        // Проверяем AuthStore быстро, без долгого ожидания
        if (!AuthStore || !AuthStore.auth) {
          // Ждем максимум 100мс для появления AuthStore
          await new Promise<void>((resolve) => {
            let attempts = 0;
            const checkAuthStore = () => {
              if (AuthStore && AuthStore.auth) {
                resolve();
              } else if (attempts++ < 10) {
                setTimeout(checkAuthStore, 10);
              } else {
                resolve();
              }
            };
            checkAuthStore();
          });
        }

        // Загружаем машины в фоне, не блокируя возврат промиса
        const loadMachines = async () => {
          // Проверяем авторизацию
          const authState = AuthStore?.auth?.state;
          console.log("[MachineService.init] Current auth state:", authState);
          
          if (authState !== "authenticated") {
            // Если пользователь в состоянии reauth или требуется переавторизация, запускаем её
            if (authState === "reauth" || (authState === "anonymous" && AuthStore?.auth?.reauthRequired)) {
              // Запускаем фоновую переавторизацию через небольшой таймаут, чтобы не блокировать
              setTimeout(() => {
                AuthService.startBackgroundReauth().catch(err => {
                  console.error("[MachineService.init] Background reauth failed:", err);
                });
              }, 100);
            }
            console.log("[MachineService.init] Skipping machine load - user not authenticated yet");
            return;
          }

          try {
            console.log("[MachineService.init] Loading machines...");
            const items = (await apiClients.http.machine.getMachines()).data;
            console.log("[MachineService.init] Machines loaded:", items.length);
            MachineStore.setItems(items);
          } catch (error) {
            console.error("[MachineService.init] Error fetching machines:", error);
          }
        };

        // Подписываемся на изменения авторизации для автоматической загрузки машин после переавторизации
        if (!this._authSubscription) {
          this._authSubscription = AuthStore.subscribe(() => {
            const authState = AuthStore.auth;
            console.log("[MachineService.init] Auth state changed:", authState.state);
            
            // Если пользователь стал авторизованным и машины еще не загружены, загружаем их
            if (authState.state === "authenticated" && MachineStore.items.length === 0) {
              console.log("[MachineService.init] User authenticated, loading machines...");
              void loadMachines();
            }
          });
        }
        
        // Пытаемся загрузить машины сразу, если пользователь уже авторизован
        void loadMachines();

        // Помечаем как инициализированный сразу, не ждем загрузки машин
        this._initialized = true;
      } finally {
        this._initPromise = null; // Очищаем промис после завершения
      }
    })();

    return this._initPromise;
  }

  /**
   * Очищает подписки и ресурсы
   */
  dispose() {
    if (this._authSubscription) {
      this._authSubscription();
      this._authSubscription = null;
    }
  }

  // Метод для сброса состояния при перезагрузке (если нужно)
  reset() {
    this._initialized = false;
    this._initPromise = null;
  }

  /**
   * Получает машину по идентификатору
   */
  async getMachineById(id: string): Promise<MachineItem | undefined> {
    return MachineStore.getMachineById(id);
  }

  /**
   * Получает список всех машин
   */
  getMachines(): MachineListItem[] {
    return MachineStore.allItems;
  }

  /**
   * Получает выбранную машину
   */
  getSelectedMachineId(): number | null {
    return MachineStore.selectedId;
  }

  setFilteredTypes(types: ServiceType[]) {
    MachineStore.setFilteredTypes(types);
  }

  selectMachine(id: number | null) {
    MachineStore.selectMachine(id);
  }
}

export const MachineService = new MachineServiceClass();

